import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStaffStore } from '../../store/staffStore.js';
import useAppStore from '../../store/useAppStore.js';
import { authApi } from '../../services/authApi.js';
import OrderCard from './OrderCard.js';
import ConfirmDialog from '../common/ConfirmDialog.js';
import RefreshButton from '../common/RefreshButton.js';
import { io } from 'socket.io-client';
import socketConfig from '../../config/socketconfig.js';

const StaffLayout = () => {
  const navigate = useNavigate();
  const {
    completeOrder,
    completeAllOrders,
    getVisibleOrders,
    getActiveOrderCount,
    initializePendingTokens,
    updateTokenFromSocket,
    removeCompletedToken,
    loading,
    error
  } = useStaffStore();
  const offlineMode = useAppStore((state) => state.offlineMode);

  const [confirmDialog, setConfirmDialog] = React.useState({ isOpen: false, orderId: null, action: 'single' });
  const [isCompletingAll, setIsCompletingAll] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const socketRef = React.useRef(null);

  useEffect(() => {
    // Load pending tokens on mount
    initializePendingTokens();

    if (offlineMode) {
      return;
    }

    // Initialize Socket.io connection
    const socketUrl = socketConfig.SOCKET_URL;
    const token = localStorage.getItem('token');

    if (!token) {
      console.error('Socket auth token missing. Ensure user is logged in before opening the staff socket.');
      return;
    }

    socketRef.current = io(socketUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: {
        token
      }
    });

    // Connection event handlers
    socketRef.current.on('connect', () => {

    });

    socketRef.current.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    socketRef.current.on('disconnect', (reason) => {
      // removed debug log
    });

    // Listen for new tokens (include walkin and waiter tokens)
    socketRef.current.on('token_issued', (data) => {
      if (data.order_type && !['walkin', 'waiter'].includes(data.order_type)) {
        return;
      }

      const audio = new Audio('/audios/notification.mp3');
      audio.play().catch((e) => console.error('Audio play failed:', e));

      updateTokenFromSocket({
        token: data.token || { token_no: data.token_id },
        order_id: data.order_id,
        order_type: data.order_type || 'walkin',
        table_id: data.table_id,
        branch_id: data.branch_id,
        items: (data.items || []).map(item => ({
          name: item.name,
          quantity: item.quantity
        })),
        status: 'issued',
        created_at: data.created_at
      });
    });

    // Listen for token status changes
    socketRef.current.on('token_event', (data) => {
      if (data.event.status === 'completed' || data.event.status === 'cancelled') {
        removeCompletedToken(data.order_id, data.event.status);
      } else {
        updateTokenFromSocket({
          token: { token_no: data.token_id },
          order_id: data.order_id,
          status: data.event.status
        });
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [initializePendingTokens, updateTokenFromSocket, removeCompletedToken, offlineMode]);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout failed:', error);
      // Proceed with local cleanup even if server logout fails
    }
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    useAppStore.getState().logout();
    navigate('/');
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await initializePendingTokens();
    } catch (error) {
      console.error('Unable to refresh staff tokens:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleMarkClick = (orderId) => {
    setConfirmDialog({ isOpen: true, orderId, action: 'single' });
  };

  const handleCompleteAllClick = () => {
    setConfirmDialog({ isOpen: true, orderId: null, action: 'all' });
  };

  const handleConfirmComplete = async () => {
    if (confirmDialog.action === 'all') {
      setIsCompletingAll(true);
      try {
        await completeAllOrders();
      } finally {
        setIsCompletingAll(false);
      }
    } else {
      await completeOrder(confirmDialog.orderId);
    }
    setConfirmDialog({ isOpen: false, orderId: null, action: 'single' });
  };

  const handleCloseDialog = () => {
    setConfirmDialog({ isOpen: false, orderId: null });
  };

  const renderContent = () => {
    const visibleOrders = getVisibleOrders();

    return (
      <div className="space-y-6">
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-2 text-red-700">
            {error}
          </div>
        )}

        <div className="rounded-xl bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
            <div className="text-sm text-gray-700">
              Active orders: <span className="font-semibold text-gray-900">{getActiveOrderCount()}</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <RefreshButton onClick={handleRefresh} loading={isRefreshing} text={true} />
              <button
                type="button"
                onClick={handleCompleteAllClick}
                disabled={loading || isCompletingAll || getActiveOrderCount() === 0}
                className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
              >
                {isCompletingAll ? 'Completing...' : 'Complete All'}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-slate-50 p-10 text-center text-gray-500">
              Loading pending tokens...
            </div>
          ) : visibleOrders.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-slate-50 p-10 text-center text-gray-500">
              No active walk-in tokens right now.
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 h-[500px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {visibleOrders.map((order) => (
                  <OrderCard key={order.id} order={order} onMarkClick={handleMarkClick} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between border-b border-gray-200 pb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Welcome, Staff</h1>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Token Management</h1>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center justify-center rounded-xl bg-red-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-600"
          >
            Logout
          </button>
        </div>

        {/* Content */}
        {renderContent()}

        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={handleCloseDialog}
          onConfirm={handleConfirmComplete}
          title="Confirm completion"
          message={confirmDialog.action === 'all'
            ? 'Are you sure you want to mark all visible orders as completed?'
            : 'Are you sure you want to mark this order as completed?'
          }
        />
      </div>
    </div>
  );
};

export default StaffLayout;